import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import { openTransportPdf } from "@/lib/pdf";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  User, Car, MapPin, Flag, Camera, ArrowLeft, ArrowRight, Save,
  Download, Check, X, Upload, AlertTriangle,
} from "lucide-react";
import { VEHICLE_TYPES, EXTRAS, FUEL_LEVELS, UREA_LEVELS, KEY_STATUSES } from "@/lib/options";
import { compressImage } from "@/lib/imageCompress";

const STEPS = [
  { id: 0, label: "Client", icon: User },
  { id: 1, label: "Ramassage", icon: MapPin },
  { id: 2, label: "Véhicule", icon: Car },
  { id: 3, label: "Livraison", icon: Flag },
  { id: 4, label: "Facturation & Photos", icon: Camera },
];

const emptyPD = { place: "", address: "", city: "", time: "", odometer: "", fuel_level: "", urea_level: "", key_status: "", key_status_other: "", instructions: "", notes: "" };
const emptyBilling = { amount: "", currency: "CAD", method: "", invoice_number: "", notes: "" };
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const emptyForm = {
  transport_date: "",
  client_name: "", po_number: "", contact_name: "", contact_email: "", contact_phone: "",
  vehicle_type: "", vehicle_make_model: "", vehicle_year: "", vehicle_color: "",
  vehicle_plate: "", vehicle_vin: "", fleet_number: "",
  vehicle_extras: [], vehicle_extras_other: "",
  pickup: { ...emptyPD },
  delivery: { ...emptyPD },
  photos: [],
  status: "en_cours",
};

export default function TransportForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAdminRoute = location.pathname.startsWith("/admin/");
  const backTo = isAdmin ? "/admin" : "/chauffeur";
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => ({ ...emptyForm, transport_date: localToday() }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentId, setCurrentId] = useState(id || null);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignedDriverId, setAssignedDriverId] = useState("");
  const [clientMode, setClientMode] = useState("__other__"); // "__other__" or a known name
  const [contactMode, setContactMode] = useState(""); // contact id or "__other__"
  const [fleetMode, setFleetMode] = useState(""); // fleet_number, "__other__" or ""
  const [plateXOpen, setPlateXOpen] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    if (isAdmin) api.get("/admin/drivers").then((r) => setDrivers(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (id) {
      api.get(`/transports/${id}`).then((r) => {
        setForm({
          ...emptyForm,
          ...r.data,
          pickup: { ...emptyPD, ...(r.data.pickup || {}) },
          delivery: { ...emptyPD, ...(r.data.delivery || {}) },
          billing: { ...emptyBilling, ...(r.data.billing || {}) },
          photos: r.data.photos || [],
        });
        setAssignedDriverId(r.data.driver_id || "");
        setCurrentId(id);
      }).catch(() => toast.error("Transport introuvable"));
    }
  }, [id]);

  // Sync clientMode with loaded client_name once clients + form are ready
  useEffect(() => {
    if (!form.client_name) { setClientMode("__other__"); return; }
    if (clients.some((c) => c.name === form.client_name)) setClientMode(form.client_name);
    else setClientMode("__other__");
  }, [clients, form.client_name]);

  // Sync contactMode with loaded contact_name/email
  useEffect(() => {
    if (!form.contact_name && !form.contact_email) { setContactMode(""); return; }
    const match = contacts.find(
      (c) => c.name === form.contact_name && (c.email || "") === (form.contact_email || "")
    );
    setContactMode(match ? match.id : "__other__");
  }, [contacts, form.contact_name, form.contact_email]);

  // Sync fleetMode with loaded fleet_number when vehicles list changes
  useEffect(() => {
    if (fleetMode === "__other__") return; // preserve manual mode
    if (!form.fleet_number) { setFleetMode(""); return; }
    setFleetMode(vehicles.some((v) => v.fleet_number === form.fleet_number) ? form.fleet_number : "__other__");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, form.fleet_number]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updPickup = (patch) => setForm((f) => ({ ...f, pickup: { ...f.pickup, ...patch } }));
  const updDelivery = (patch) => setForm((f) => ({ ...f, delivery: { ...f.delivery, ...patch } }));
  const toggleExtra = (x) =>
    setForm((f) => ({ ...f, vehicle_extras: f.vehicle_extras.includes(x) ? f.vehicle_extras.filter(e => e !== x) : [...f.vehicle_extras, x] }));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = isAdmin && assignedDriverId ? { ...form, driver_id: assignedDriverId } : form;
      let res;
      if (currentId) res = await api.put(`/transports/${currentId}`, payload);
      else res = await api.post("/transports", payload);
      setCurrentId(res.data.id);
      if (!silent) toast.success("Enregistré");
      return res.data.id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      // Ensure record exists so photos can persist
      let tid = currentId;
      if (!tid) tid = await save(true);
      const newIds = [];
      for (const raw of files) {
        const file = await compressImage(raw, { maxWidth: 1920, maxHeight: 1920, quality: 0.75 });
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(data.id);
      }
      const nextPhotos = [...form.photos, ...newIds];
      setForm((f) => ({ ...f, photos: nextPhotos }));
      // persist photo ids
      await api.put(`/transports/${tid}`, { ...form, photos: nextPhotos });
      toast.success(`${newIds.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec du téléversement");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (fid) => {
    const nextPhotos = form.photos.filter((p) => p !== fid);
    setForm((f) => ({ ...f, photos: nextPhotos }));
    if (currentId) await api.put(`/transports/${currentId}`, { ...form, photos: nextPhotos });
  };

  const submit = async () => {
    upd({ status: "termine" });
    const tid = await save(true);
    if (!tid) return;
    try {
      const { data } = await api.post(`/transports/${tid}/finalize`);
      if (data.email?.ok) {
        const list = Array.isArray(data.email.to) ? data.email.to : [data.email.to];
        toast.success(`Bon de transport terminé — PDF envoyé à ${list.join(", ")}`);
        if (data.email?.failed?.length) {
          toast.warning(`Non livré à : ${data.email.failed.map(f=>f.to).join(", ")}`);
        }
      } else {
        toast.success("Bon de transport terminé");
        if (data.email?.message) toast.info(data.email.message);
      }
      nav(backTo);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la finalisation");
    }
  };

  const canNext = () => {
    if (step === 0) return form.client_name.trim() && form.transport_date;
    return true;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" className="rounded-none" onClick={() => nav(backTo)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="label-caps text-muted-foreground">{currentId ? "Édition" : "Nouveau"}</div>
            <h1 className="font-display font-black text-3xl md:text-4xl leading-none tracking-tight">Bon de transport</h1>
          </div>
        </div>

        {isAdmin && (
          <Card className="border-2 rounded-none p-4 mb-4 bg-secondary/40">
            <Label className="label-caps mb-2 block">Chauffeur assigné</Label>
            <Select value={assignedDriverId || undefined} onValueChange={(v) => setAssignedDriverId(v)}>
              <SelectTrigger data-testid="f-driver-assign" className="h-11 border-2 rounded-none bg-white">
                <SelectValue placeholder="Choisir un chauffeur" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name || d.email} — {d.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Sélectionnez le chauffeur qui prendra ce transport. Vous pouvez le réassigner à tout moment.
            </p>
          </Card>
        )}

        {/* Stepper */}
        <div className="mb-8 grid grid-cols-5 gap-1">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const active = s.id === step;
            const done = s.id < step;
            return (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                data-testid={`step-${s.id}`}
                className={`p-3 border-2 flex flex-col items-center gap-1 text-center transition-colors ${
                  active ? "bg-primary text-primary-foreground border-primary"
                  : done ? "bg-black text-white border-black"
                  : "bg-card border-border hover:border-black"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="label-caps text-[10px]">{s.label}</span>
              </button>
            );
          })}
        </div>

        <Card className="border-2 rounded-none p-6 md:p-8">
          {step === 0 && (
            <div className="space-y-5">
              <SectionTitle icon={User}>Client & Contact</SectionTitle>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Date du transport">
                  <Input data-testid="f-date" type="date" value={form.transport_date} onChange={(e)=>upd({transport_date:e.target.value})} className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Nom du client *">
                  <Select
                    value={clientMode}
                    onValueChange={(v) => {
                      setClientMode(v);
                      if (v === "__other__") upd({ client_name: "" });
                      else upd({ client_name: v });
                    }}
                  >
                    <SelectTrigger data-testid="f-client-select" className="h-12 border-2 rounded-none">
                      <SelectValue placeholder="Choisir un client" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="__other__">Autre…</SelectItem>
                    </SelectContent>
                  </Select>
                  {clientMode === "__other__" && (
                    <Input
                      data-testid="f-client"
                      required
                      value={form.client_name}
                      onChange={(e) => upd({ client_name: e.target.value })}
                      placeholder="Saisir le nom du client"
                      className="h-12 border-2 rounded-none mt-2"
                    />
                  )}
                </Field>
                <Field label="N° P.O.">
                  <Input data-testid="f-po" value={form.po_number} onChange={(e)=>upd({po_number:e.target.value})} placeholder="Bon de commande" className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Nom du contact">
                  <Select
                    value={contactMode || undefined}
                    onValueChange={(v) => {
                      setContactMode(v);
                      if (v === "__other__") {
                        upd({ contact_name: "", contact_email: "", contact_phone: "" });
                        return;
                      }
                      const c = contacts.find((x) => x.id === v);
                      if (!c) return;
                      upd({ contact_name: c.name || "", contact_email: c.email || "", contact_phone: c.phone || "" });
                    }}
                  >
                    <SelectTrigger data-testid="f-contact-select" className="h-12 border-2 rounded-none">
                      <SelectValue placeholder="Choisir un contact" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {contacts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}{c.email ? ` — ${c.email}` : ""}
                        </SelectItem>
                      ))}
                      <SelectItem value="__other__">Autre…</SelectItem>
                    </SelectContent>
                  </Select>
                  {contactMode === "__other__" && (
                    <Input
                      data-testid="f-contact-name"
                      value={form.contact_name}
                      onChange={(e)=>upd({contact_name:e.target.value})}
                      placeholder="Nom du contact"
                      className="h-12 border-2 rounded-none mt-2"
                    />
                  )}
                </Field>
                <Field label="Courriel du contact">
                  <Input
                    data-testid="f-contact-email"
                    type="email"
                    value={form.contact_email}
                    onChange={(e)=>upd({contact_email:e.target.value})}
                    disabled={!!contactMode && contactMode !== "__other__"}
                    className="h-12 border-2 rounded-none disabled:opacity-70"
                  />
                </Field>
                <Field label="Téléphone du contact">
                  <Input
                    data-testid="f-contact-phone"
                    value={form.contact_phone}
                    onChange={(e)=>upd({contact_phone:e.target.value})}
                    disabled={!!contactMode && contactMode !== "__other__"}
                    className="h-12 border-2 rounded-none disabled:opacity-70"
                  />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <SectionTitle icon={Car}>Véhicule</SectionTitle>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Type de véhicule">
                  <SelectField testId="f-vtype" value={form.vehicle_type} onChange={(v)=>upd({vehicle_type:v})} options={VEHICLE_TYPES} placeholder="Choisir un type" />
                </Field>
                <Field label="N° de flotte">
                  <Select
                    value={fleetMode || undefined}
                    onValueChange={(v) => {
                      setFleetMode(v);
                      if (v === "__other__") {
                        upd({ fleet_number: "" });
                        return;
                      }
                      const veh = vehicles.find((x) => x.fleet_number === v);
                      if (!veh) return;
                      upd({
                        fleet_number: veh.fleet_number,
                        vehicle_type: veh.vehicle_type || form.vehicle_type,
                        vehicle_make_model: veh.vehicle_make_model || "",
                        vehicle_year: veh.vehicle_year || "",
                        vehicle_color: veh.vehicle_color || "",
                        vehicle_plate: veh.vehicle_plate || "",
                        vehicle_vin: veh.vehicle_vin || "",
                      });
                    }}
                  >
                    <SelectTrigger data-testid="f-fleet-select" className="h-12 border-2 rounded-none">
                      <SelectValue placeholder="Choisir un n° de flotte" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.fleet_number}>
                          {v.fleet_number}{v.vehicle_make_model ? ` — ${v.vehicle_make_model}` : ""}
                        </SelectItem>
                      ))}
                      <SelectItem value="__other__">Autre…</SelectItem>
                    </SelectContent>
                  </Select>
                  {fleetMode === "__other__" && (
                    <Input
                      data-testid="f-fleet"
                      value={form.fleet_number}
                      onChange={(e)=>upd({fleet_number:e.target.value})}
                      placeholder="Saisir le n° de flotte"
                      className="h-12 border-2 rounded-none mt-2"
                    />
                  )}
                </Field>
                <Field label="Marque / Modèle">
                  <Input data-testid="f-model" value={form.vehicle_make_model} onChange={(e)=>upd({vehicle_make_model:e.target.value})} className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Année">
                  <Input data-testid="f-year" value={form.vehicle_year} onChange={(e)=>upd({vehicle_year:e.target.value})} className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Couleur">
                  <Input data-testid="f-color" value={form.vehicle_color} onChange={(e)=>upd({vehicle_color:e.target.value})} className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Plaque">
                  <Input data-testid="f-plate" value={form.vehicle_plate} onChange={(e)=>upd({vehicle_plate:e.target.value})} className="h-12 border-2 rounded-none uppercase" />
                </Field>
                <Field label="N° de série (NIV)">
                  <Input data-testid="f-vin" value={form.vehicle_vin} onChange={(e)=>upd({vehicle_vin:e.target.value.toUpperCase()})} className="h-12 border-2 rounded-none uppercase" />
                </Field>
              </div>

              <div>
                <Label className="label-caps mb-3 block">Extras (choix multiples)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {EXTRAS.map((x) => {
                    const on = form.vehicle_extras.includes(x);
                    return (
                      <button
                        key={x}
                        type="button"
                        onClick={()=>toggleExtra(x)}
                        data-testid={`extra-${x}`}
                        className={`flex items-center gap-2 px-3 py-3 border-2 text-sm font-semibold text-left transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-black"}`}
                      >
                        <span className={`w-5 h-5 border-2 flex items-center justify-center ${on ? "bg-white border-white" : "border-current"}`}>
                          {on && <Check className="w-3 h-3 text-primary" />}
                        </span>
                        <span>{x}</span>
                      </button>
                    );
                  })}
                </div>
                {form.vehicle_extras.includes("Matière dangereuse") && (
                  <div data-testid="hazmat-weight-notice" className="mt-3 flex items-start gap-2 border-2 border-amber-500 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    <span>S'assurer de la conformité du poids total dans le camion</span>
                  </div>
                )}
                {form.vehicle_extras.includes("Autre") && (
                  <div className="mt-3">
                    <Label className="label-caps mb-2 block">Précisez « Autre »</Label>
                    <Input
                      data-testid="f-extras-other"
                      value={form.vehicle_extras_other}
                      onChange={(e)=>upd({vehicle_extras_other:e.target.value})}
                      placeholder="Décrivez l'extra"
                      className="h-12 border-2 rounded-none"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <PickupDeliverySection title="Ramassage" icon={MapPin} data={form.pickup} onChange={updPickup} prefix="pickup" locations={locations} />
          )}
          {step === 3 && (
            <PickupDeliverySection title="Livraison" icon={Flag} data={form.delivery} onChange={updDelivery} prefix="delivery" locations={locations} />
          )}

          {step === 4 && (
            <div className="space-y-6">
              <SectionTitle icon={Camera}>Facturation & Photos</SectionTitle>

              <div className="space-y-4 pb-6 border-b-2 border-border">
                <h3 className="font-display font-bold text-lg">Facturation</h3>
                <Field label="Montant (CAD)">
                  <Input data-testid="b-amount" type="number" step="0.01" value={form.billing?.amount || ""} onChange={(e)=>setForm(f=>({...f,billing:{...(f.billing||emptyBilling),amount:e.target.value}}))} className="h-12 border-2 rounded-none" />
                </Field>
                <Field label="Notes de facturation">
                  <Textarea data-testid="b-notes" value={form.billing?.notes || ""} onChange={(e)=>setForm(f=>({...f,billing:{...(f.billing||emptyBilling),notes:e.target.value}}))} className="min-h-20 border-2 rounded-none" />
                </Field>
              </div>

              <h3 className="font-display font-bold text-lg">Photos du véhicule</h3>
              <p className="text-sm text-muted-foreground">Ajoutez des photos de l'état du véhicule, de l'odomètre et de tout dommage visible. Elles seront incluses dans le PDF.</p>

              {form.vehicle_extras?.includes("Mettre du carburant") && (
                <div
                  data-testid="fuel-receipt-reminder"
                  className="border-2 border-primary bg-primary/10 p-4 flex items-start gap-3"
                >
                  <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Camera className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-black text-base leading-tight">Rappel : photo du reçu de carburant</div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Vous avez coché <strong className="text-foreground">Mettre du carburant</strong>. Prenez en photo le
                      reçu de la pompe et ajoutez-le ci-dessous pour l'inclure au bon de transport.
                    </p>
                  </div>
                </div>
              )}

              <label
                htmlFor="photo-input"
                className="block border-2 border-dashed border-border hover:border-primary p-10 text-center cursor-pointer transition-colors bg-secondary/40"
                data-testid="photo-dropzone"
              >
                <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display font-bold text-lg">Toucher pour ajouter des photos</div>
                <div className="text-xs text-muted-foreground mt-1">JPG, PNG, HEIC</div>
                <input id="photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
              </label>
              {uploading && <div className="label-caps text-primary">Téléversement…</div>}

              {form.photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {form.photos.map((fid) => (
                    <div key={fid} className="relative border-2 border-border aspect-square">
                      <AuthImage fileId={fid} className="w-full h-full object-cover" />
                      <button
                        onClick={()=>removePhoto(fid)}
                        className="absolute top-1 right-1 bg-black text-white p-1 hover:bg-destructive"
                        data-testid={`remove-photo-${fid}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {currentId && (
                <div className="flex flex-col md:flex-row gap-3 pt-4 border-t-2 border-border">
                  <Button
                    variant="outline"
                    className="h-12 rounded-none border-2 w-full md:w-auto"
                    onClick={() => openTransportPdf(currentId)}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Nav */}
        <div className="flex items-center justify-between mt-6 gap-3">
          <Button
            variant="outline"
            className="h-12 rounded-none border-2"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Précédent
          </Button>

          <Button
            variant="outline"
            className="h-12 rounded-none border-2"
            onClick={() => save()}
            disabled={saving}
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "…" : "Enregistrer"}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button
              className="h-12 rounded-none bg-primary text-primary-foreground btn-hard active:scale-95"
              onClick={async () => { if (canNext()) { await save(true); setStep((s) => s + 1); } else toast.error("Complétez les champs requis"); }}
              data-testid="next-btn"
            >
              Suivant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="h-12 rounded-none bg-black text-white hover:bg-black/80 btn-hard active:scale-95"
              onClick={() => (form.vehicle_extras.includes("Plaque X") ? setPlateXOpen(true) : submit())}
              data-testid="submit-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Terminer
            </Button>
          )}
        </div>
      </div>
      <AlertDialog open={plateXOpen} onOpenChange={setPlateXOpen}>
        <AlertDialogContent data-testid="plate-x-dialog" className="rounded-none border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-display font-black">
              <AlertTriangle className="w-5 h-5 text-primary" /> Plaque X
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-foreground font-semibold">
              Ne pas oublier de reprendre la plaque X.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plate-x-cancel" className="rounded-none border-2">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="plate-x-confirm"
              className="rounded-none bg-black text-white hover:bg-black/80"
              onClick={() => { setPlateXOpen(false); submit(); }}
            >
              Confirmer et terminer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b-2 border-border">
      <div className="w-10 h-10 bg-primary flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
      </div>
      <h2 className="font-display font-black text-2xl leading-none">{children}</h2>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="label-caps">{label}</Label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-12 border-2 rounded-none">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-none">
        {options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

function PickupDeliverySection({ title, icon, data, onChange, prefix, locations = [] }) {
  const [placeMode, setPlaceMode] = useState(() => {
    if (!data.place) return "";
    return locations.some((l) => l.name === data.place) ? data.place : "__other__";
  });
  // Re-sync when locations load or data.place changes from outside (e.g. record load)
  useEffect(() => {
    if (placeMode === "__other__") return; // preserve manual mode
    if (!data.place) { setPlaceMode(""); return; }
    setPlaceMode(locations.some((l) => l.name === data.place) ? data.place : "__other__");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, data.place]);

  const onPickLocation = (v) => {
    setPlaceMode(v);
    if (v === "__other__") {
      onChange({ place: "" });
      return;
    }
    const loc = locations.find((l) => l.name === v);
    if (!loc) return;
    onChange({ place: loc.name, address: loc.address || "", city: loc.city || "" });
  };
  return (
    <div className="space-y-5">
      <SectionTitle icon={icon}>{title}</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Lieu">
          <Select value={placeMode || undefined} onValueChange={onPickLocation}>
            <SelectTrigger data-testid={`${prefix}-place-select`} className="h-12 border-2 rounded-none">
              <SelectValue placeholder="Choisir un lieu" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {locations.map((l) => (
                <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
              ))}
              <SelectItem value="__other__">Autre…</SelectItem>
            </SelectContent>
          </Select>
          {placeMode === "__other__" && (
            <Input
              data-testid={`${prefix}-place`}
              value={data.place}
              onChange={(e) => onChange({ place: e.target.value })}
              placeholder="Saisir le nom du lieu"
              className="h-12 border-2 rounded-none mt-2"
            />
          )}
        </Field>
        <Field label="Heure">
          <Input data-testid={`${prefix}-time`} type="time" value={data.time} onChange={(e)=>onChange({time:e.target.value})} className="h-12 border-2 rounded-none" />
        </Field>
        <Field label="Adresse">
          <Input data-testid={`${prefix}-address`} value={data.address} onChange={(e)=>onChange({address:e.target.value})} className="h-12 border-2 rounded-none" />
        </Field>
        <Field label="Ville">
          <Input data-testid={`${prefix}-city`} value={data.city} onChange={(e)=>onChange({city:e.target.value})} className="h-12 border-2 rounded-none" />
        </Field>
        <Field label="Odomètre (km)">
          <Input data-testid={`${prefix}-odo`} type="number" value={data.odometer} onChange={(e)=>onChange({odometer:e.target.value})} className="h-12 border-2 rounded-none" />
        </Field>
        <Field label="Carburant / Batterie">
          <SelectField testId={`${prefix}-fuel`} value={data.fuel_level} onChange={(v)=>onChange({fuel_level:v})} options={FUEL_LEVELS} placeholder="Choisir un niveau" />
        </Field>
        <Field label="Niveau d'urée">
          <SelectField testId={`${prefix}-urea`} value={data.urea_level} onChange={(v)=>onChange({urea_level:v})} options={UREA_LEVELS} placeholder="Choisir un niveau" />
        </Field>
        <Field label="Clé">
          <SelectField testId={`${prefix}-key`} value={data.key_status} onChange={(v)=>onChange({key_status:v})} options={KEY_STATUSES} placeholder="État de la clé" />
        </Field>
      </div>
      {data.key_status === "Autre" && (
        <Field label="Précisez « Autre » (clé)">
          <Input
            data-testid={`${prefix}-key-other`}
            value={data.key_status_other || ""}
            onChange={(e)=>onChange({key_status_other:e.target.value})}
            placeholder="Décrivez où / comment"
            className="h-12 border-2 rounded-none"
          />
        </Field>
      )}
      <Field label="Consignes du client">
        <Textarea data-testid={`${prefix}-instructions`} value={data.instructions} onChange={(e)=>onChange({instructions:e.target.value})} className="min-h-24 border-2 rounded-none" />
      </Field>
      <Field label="Notes">
        <Textarea data-testid={`${prefix}-notes`} value={data.notes || ""} onChange={(e)=>onChange({notes:e.target.value})} placeholder="Observations, remarques internes…" className="min-h-24 border-2 rounded-none" />
      </Field>
    </div>
  );
}
